import NextLink from "next/link";
import { Box } from "@chakra-ui/react";
import { customEvent, getCdnImageSrc, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

/**
 * 장례식장 상세에 맞는 가이드북 글. 전부 운영 sitemap 에 있는 주소이고 제목은 운영 페이지 title 기준.
 * 숫자 index 주소는 서버 데이터라 글이 바뀌면 제목도 같이 고쳐야 한다.
 * 썸네일은 가이드북 상세와 같은 CDN 규칙 images/guidebook/tip/thumbnail/{category}/{index}.png (6개 모두 200 확인).
 */
const GUIDES = [
  {
    href: "/guidebook/tip/before/2/",
    thumbnail: "before/2",
    title: "장례식장 비용은 얼마나 나올까?",
  },
  {
    href: "/guidebook/tip/ongoing1/2/",
    thumbnail: "ongoing1/2",
    title: "장례식장 계약을 위한 필수 체크리스트 5가지",
  },
  {
    href: "/guidebook/tip/before/1/",
    thumbnail: "before/1",
    title: "장례식장 분류 총정리! 장례식장의 종류와 특징",
  },
  {
    href: "/guidebook/tip/before/funeral-expense/",
    thumbnail: "before/funeral-expense",
    title: "장례식 비용 3분 안에 정리하기",
  },
  {
    href: "/guidebook/tip/before/funeral-cost-save/",
    thumbnail: "before/funeral-cost-save",
    title: "장례 비용 줄이는 법 핵심요약",
  },
  {
    href: "/guidebook/tip/before/family-funeral/",
    thumbnail: "before/family-funeral",
    title: "가족장이란? 비용 상세 및 주의할점",
  },
];

const PICK_COUNT = 3;

/**
 * 장례식장 id 를 시드로 섞어 3개를 고른다. 1,184 페이지가 같은 카드 묶음을 갖지 않게 하려는 것.
 * 접속마다 무작위로 바꾸면 SSG HTML 과 클라이언트 렌더가 달라 hydration 에러가 나고,
 * 크롤러는 어차피 빌드 때 한 가지만 본다. 그래서 같은 페이지는 항상 같은 조합, 같은 순서다.
 */
const pickGuides = (parlorId: number) => {
  // mulberry32. 곱셈은 Math.imul 로 32비트 안에서 한다. 일반 곱셈은 2^53 을 넘어 정밀도가 깨져
  // 이웃한 id 끼리 같은 조합이 나온다(LCG 로 했을 때 28번과 30번이 같았음)
  let seed = parlorId || 1;
  const next = () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), seed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const shuffled = [...GUIDES];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, PICK_COUNT);
};

/** 목록만 그린다. 제목과 위치는 ParlorSeoTextSection 의 SectionWrapper 가 정한다 */
function ParlorGuideCards({ parlorId }: { parlorId: number }) {
  return (
    <BaseStack width="100%" spacing={8} color={baseColors.label_normal}>
      {pickGuides(parlorId).map((guide, index) => (
        <NextLink
          key={guide.href}
          href={guide.href}
          onClick={() =>
            customEvent({
              newGtm: {
                path: "parlor_detail",
                trigger_and_target: "guide_card",
                properties: { parlor_id: parlorId, href: guide.href, position: index + 1 },
              },
            })
          }
        >
          {/* 썸네일이 테두리에 바로 붙도록 카드에는 여백을 두지 않고 글자 쪽에만 둔다 */}
          <BaseStack
            direction="horizontal"
            alignItems="stretch"
            cursor="pointer"
            border={`1px solid ${baseColors.line_normal}`}
            borderRadius="8px"
            overflow="hidden"
          >
            {/* 원본 비율(3:2) 그대로. 무빈소 퍼널 가이드 카드와 같은 img + CDN 방식 */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={getCdnImageSrc(`images/guidebook/tip/thumbnail/${guide.thumbnail}.png`)}
              alt={guide.title}
              width={96}
              height={64}
              loading="lazy"
              style={{
                width: 96,
                height: 64,
                flexShrink: 0,
                display: "block",
                objectFit: "cover",
                backgroundColor: baseColors.fill_normal,
              }}
            />
            <Box flex={1} minWidth={0} padding="8px 12px" alignSelf="center">
              <Box className="label_2_m" color={baseColors.label_assistive_2}>
                고이 장례 가이드북
              </Box>
              <Spacing gap={2} />
              <Box className="body_2_normal_sb">{guide.title}</Box>
            </Box>
          </BaseStack>
        </NextLink>
      ))}
    </BaseStack>
  );
}

export default ParlorGuideCards;
